import React from "react";
import { makeStyles } from "@material-ui/core";
import { Container, Grid, Typography } from "@material-ui/core";
import "./Terms.css";

// Images
import backgroundImage from "../../../Images/Hero/BackgroundImage.jpg";

const useStyles = makeStyles((theme) => ({
  terms: {
    minHeight: "100vh",
    width: "100%",
    overflow: "hidden",
    display: "flex",
    background:
      "linear-gradient(61.54deg, #e5e7eb -48.56%, #e5e7eb -48.55%, #f8fbfa 72.22%)",
    position: "relative",
    alignItems: "flex-start",
    paddingTop: "100px",
    paddingBottom: "100px",
  },
  heroImage: {
    backgroundImage: `url(${backgroundImage})`,
    opacity: "0.3",
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: "0",
  },
  container: {
    zIndex: "1",
    padding: "0 24px",
    [theme.breakpoints.down("sm")]: {
      margin: "30px auto 20px",
    },
    [theme.breakpoints.down("xs")]: {
      margin: "20px auto 40px",
    },
  },
  title: {
    fontWeight: "700 !important",
    fontFamily: "Roboto !important",
    fontSize: "46px !important",
    textTransform: "capitalize !important",
    marginBottom: "10px !important",
    color: "#2D2D2D !important",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "36px !important",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "28px !important",
    },
  },
  titleHighlight: {
    background: "var(--color-accent-orange)",
    height: "15px",
    width: "100%",
    maxWidth: "400px",
    margin: "0 auto 40px",
    [theme.breakpoints.down("xs")]: {
      marginBottom: "30px",
    },
  },
  sectionTitle: {
    fontWeight: "700 !important",
    fontFamily: "Roboto !important",
    fontSize: "24px !important",
    color: "#07516C !important",
    marginTop: "40px !important",
    marginBottom: "20px !important",
    [theme.breakpoints.down("xs")]: {
      fontSize: "20px !important",
      marginTop: "30px !important",
    },
  },
  paragraph: {
    fontWeight: "400 !important",
    fontSize: "16px !important",
    lineHeight: "1.6 !important",
    marginBottom: "20px !important",
    fontFamily: "Roboto !important",
    color: "#2D2D2D !important",
    [theme.breakpoints.down("xs")]: {
      fontSize: "15px !important",
    },
  },
  listItem: {
    fontWeight: "400 !important",
    fontSize: "16px !important",
    lineHeight: "1.6 !important",
    marginBottom: "10px !important",
    fontFamily: "Roboto !important",
    color: "#2D2D2D !important",
    paddingLeft: "20px !important",
    position: "relative",
    "&:before": {
      content: '"•"',
      position: "absolute",
      left: "0",
      color: "#07516C",
      fontWeight: "bold",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "15px !important",
    },
  },
  lastUpdated: {
    fontWeight: "400 !important",
    fontSize: "14px !important",
    fontStyle: "italic !important",
    color: "var(--color-neutral-500) !important",
    marginTop: "40px !important",
    textAlign: "center",
    fontFamily: "Roboto !important",
  },
  contentContainer: {
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "8px",
    padding: "40px",
    [theme.breakpoints.down("sm")]: {
      padding: "30px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "20px",
    },
  },
}));

const Terms = () => {
  const classes = useStyles();
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className={classes.terms}>
      <div className={classes.heroImage}></div>
      <Container maxWidth="lg" className={classes.container}>
        <Grid container>
          <Grid item xs={12}>
            <div className={classes.contentContainer}>
              <Typography variant="h1" className={classes.title}>
                Terms and Conditions
              </Typography>
              <div className={classes.titleHighlight}></div>

              <Typography variant="h2" className={classes.sectionTitle}>
                1. Acceptance of Terms
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                By accessing and using the Kinetx Co LLC website ("Website," "we," "us," or "our"), 
                you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, 
                please do not use our Website.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                2. Website Content and Purpose
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                This Website provides information about Kinetx Co LLC's IT infrastructure services and allows 
                visitors to learn about our company, view our service offerings, and contact us for business inquiries. 
                The Website includes:
              </Typography>
              <div className={classes.listItem}>Company information and service descriptions</div>
              <div className={classes.listItem}>Contact forms and business inquiry tools</div>
              <div className={classes.listItem}>Educational content about IT infrastructure</div>
              <div className={classes.listItem}>Client testimonials and case studies</div>
              <div className={classes.listItem}>Industry insights and resources</div>

              <Typography variant="h2" className={classes.sectionTitle}>
                3. Website Usage Terms
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                You agree to use our Website only for lawful purposes and in accordance with these Terms. 
                You are prohibited from using our Website:
              </Typography>
              <div className={classes.listItem}>For any unlawful purpose or to solicit others to perform unlawful acts</div>
              <div className={classes.listItem}>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</div>
              <div className={classes.listItem}>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</div>
              <div className={classes.listItem}>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</div>
              <div className={classes.listItem}>To submit false or misleading information through contact forms</div>
              <div className={classes.listItem}>To attempt to gain unauthorized access to any part of the Website or its related systems</div>
              <div className={classes.listItem}>To interfere with or disrupt the Website's functionality or servers</div>

              <Typography variant="h2" className={classes.sectionTitle}>
                4. User Communications and Data
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                When you contact us through our Website's contact forms or other communication methods, you agree that:
              </Typography>
              <div className={classes.listItem}>The information you provide is accurate and truthful</div>
              <div className={classes.listItem}>We may use your contact information to respond to your inquiries</div>
              <div className={classes.listItem}>We may retain your communications for business purposes</div>
              <div className={classes.listItem}>You consent to our collection and use of this data as outlined in our Privacy Policy</div>

              <Typography variant="h2" className={classes.sectionTitle}>
                5. Website Content and Intellectual Property
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                All content on this Website, including but not limited to text, graphics, images, logos, videos, 
                and software, is the exclusive property of Kinetx Co LLC and is protected by copyright, trademark, 
                and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative 
                works from any Website content without our prior written consent.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                6. Website Availability and Technical Issues
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                While we strive to maintain the Website's availability, we do not guarantee uninterrupted access. 
                The Website may be temporarily unavailable due to maintenance, technical issues, or circumstances 
                beyond our control. We are not liable for any inconvenience or loss resulting from Website downtime.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                7. Disclaimer of Warranties
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                The information on this Website is provided on an "as is" basis. To the fullest extent permitted by law, 
                Kinetx Co LLC excludes all representations, warranties, conditions and terms related to our Website and 
                the use of this Website. We do not warrant that the Website will be error-free, secure, or free from 
                viruses or other harmful components.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                8. Limitation of Liability
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                In no event shall Kinetx Co LLC, nor its directors, employees, partners, agents, suppliers, or affiliates, 
                be liable for any indirect, incidental, special, consequential, or punitive damages, including without 
                limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use 
                of the Website or inability to access the Website.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                9. Privacy and Data Collection
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of 
                our Website, to understand how we collect, use, and protect your personal information when you visit 
                our Website or submit information through our contact forms.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                10. Access Restrictions
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We reserve the right to restrict or terminate your access to our Website immediately, without prior 
                notice, if you violate these Terms or engage in any activity that we deem harmful to the Website, 
                its users, or our business. Upon restriction, your right to use the Website will cease immediately.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                11. External Links
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                Our Website may contain links to third-party websites or services that are not owned or controlled 
                by Kinetx Co LLC. We have no control over and assume no responsibility for the content, privacy policies, 
                or practices of any third-party websites or services. We encourage you to review the terms and privacy 
                policies of any third-party sites you visit.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                12. Governing Law
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                These Terms shall be interpreted and governed by the laws of the State of New York, United States, 
                without regard to conflict of law provisions. Any disputes arising under these Terms and relating to 
                your use of the Website shall be subject to the exclusive jurisdiction of the courts located in New York.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                13. Changes to Terms
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will 
                try to provide at least 30 days' notice by posting the updated Terms on our Website. What constitutes 
                a material change will be determined at our sole discretion. Your continued use of the Website after 
                any changes constitutes acceptance of the new Terms.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                14. Contact Information
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                If you have any questions about these Terms and Conditions for Website usage, please contact us at:
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                <strong>Kinetx Co LLC</strong><br />
                Email: techs@kinetx.co<br />
                Phone: 917-719-4889<br />
                Website: kinetx.co
              </Typography>

              <Typography variant="body2" className={classes.lastUpdated}>
                Last updated: {currentDate}
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default Terms;
